import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import { useOrganizationWageSetMutationHandlers } from "../hooks/use-organization-wage-set-mutations";
import { getTodayInTimezone, isCalendarDate } from "../utils/wage-dates";
import { DateField } from "./date-field";
import { OrganizationWageSetDialog } from "./organization-wage-set-dialog";

enum AssigneeKind {
  Provider = "provider",
  Pool = "pool",
}

type AssignOrganizationWageSetDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationWageSetId: string;
};

function isAssigneeKind(value: string): value is AssigneeKind {
  return value === AssigneeKind.Provider || value === AssigneeKind.Pool;
}

export function AssignOrganizationWageSetDialog({
  open,
  onOpenChange,
  organizationWageSetId,
}: AssignOrganizationWageSetDialogProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { invalidateOrganizationWageSets, handleError } =
    useOrganizationWageSetMutationHandlers();
  const [kind, setKind] = useState(AssigneeKind.Provider);
  const [assigneeId, setAssigneeId] = useState("");
  const [startsOn, setStartsOn] = useState(() =>
    getTodayInTimezone({ timezoneId: selectedOrgBusinessUnit.timezoneId }),
  );
  const [assigneeError, setAssigneeError] = useState<string>();
  const [dateError, setDateError] = useState<string>();

  const providersQuery = useQuery(
    trpc.organizationBusinessUnitAffiliation.listForBusinessUnit.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    }),
  );
  const poolsQuery = useQuery(
    trpc.providerPool.list.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    }),
  );

  const assignMutation = useMutation(
    trpc.organizationWageSet.assign.mutationOptions({
      onSuccess: async () => {
        await invalidateOrganizationWageSets();
        toast.success("Wage set assigned");
        onOpenChange(false);
      },
      onError: handleError,
    }),
  );

  function handleKindChange(value: string) {
    if (isAssigneeKind(value)) {
      setKind(value);
      setAssigneeId("");
    }
  }

  function handleSubmit() {
    const nextAssigneeError = assigneeId ? undefined : "Choose who to assign";
    const nextDateError = isCalendarDate(startsOn)
      ? undefined
      : "Choose a start date";
    setAssigneeError(nextAssigneeError);
    setDateError(nextDateError);
    if (nextAssigneeError || nextDateError) {
      return;
    }
    const target =
      kind === AssigneeKind.Provider
        ? { providerUserId: assigneeId }
        : { providerPoolId: assigneeId };
    assignMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      organizationWageSetId,
      target,
      startsOn,
    });
  }

  const isProvider = kind === AssigneeKind.Provider;
  const options = isProvider
    ? (providersQuery.data ?? []).map((row) => ({
        id: row.userId,
        label:
          [row.firstName, row.lastName].filter(Boolean).join(" ") || row.email,
      }))
    : (poolsQuery.data ?? []).map((pool) => ({
        id: pool.id,
        label: pool.name,
      }));
  const isLoadingOptions = isProvider
    ? providersQuery.isLoading
    : poolsQuery.isLoading;
  const emptyLabel = isProvider ? "No hired providers" : "No provider pools";

  return (
    <OrganizationWageSetDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Assign wage set"
      description="Assign this wage set to a hired provider, or to every member of a provider pool."
      submitLabel="Assign"
      isPending={assignMutation.isPending}
      onSubmit={handleSubmit}
    >
      <div className="space-y-1.5">
        <Label htmlFor="assignee-kind">Assign to</Label>
        <Select value={kind} onValueChange={handleKindChange}>
          <SelectTrigger id="assignee-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AssigneeKind.Provider}>
              Hired provider
            </SelectItem>
            <SelectItem value={AssigneeKind.Pool}>Provider pool</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="assignee">{isProvider ? "Provider" : "Pool"}</Label>
        <Select value={assigneeId} onValueChange={setAssigneeId}>
          <SelectTrigger id="assignee" aria-invalid={Boolean(assigneeError)}>
            <SelectValue
              placeholder={isLoadingOptions ? "Loading..." : `Select a ${kind}`}
            />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
            {!isLoadingOptions && options.length === 0 && (
              <div className="px-2 py-1.5 text-sm text-slate-500">
                {emptyLabel}
              </div>
            )}
          </SelectContent>
        </Select>
        {assigneeError && (
          <p className="text-sm text-red-600">{assigneeError}</p>
        )}
      </div>
      <DateField
        label="Starts on"
        value={startsOn}
        onChange={setStartsOn}
        error={dateError}
      />
    </OrganizationWageSetDialog>
  );
}
